import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Business, Person } from '@mui/icons-material';

interface ProviderData {
  provider: any;
  is_claimed: boolean;
  claim_info?: { username?: string } | null;
}

const ProviderPage = () => {
  const { npi } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProviderData | null>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  useEffect(() => {
    const load = async () => {
      if (!npi) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/api/providers/${npi}`);
        if (!res.ok) throw new Error('Provider not found');
        const json = await res.json();
        setData(json);
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [npi]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h5">{error || 'Provider not found'}</Typography>
      </Container>
    );
  }

  const p = data.provider;
  const name = p.entity_type_code === 1
    ? `${p.first_name} ${p.middle_name || ''} ${p.last_name}`.trim()
    : p.organization_name;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 64, height: 64 }}>
              {p.entity_type_code === 1 ? <Person /> : <Business />}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h5" fontWeight={700}>{name}</Typography>
              <Stack direction="row" spacing={1} mt={1}>
                {p.credential && <Chip label={p.credential} />}
                {p.primary_specialty && <Chip label={p.primary_specialty} />}
                {p.practice_city && <Chip label={`${p.practice_city}, ${p.practice_state}`} />}
              </Stack>
            </Box>
            {data.is_claimed ? (
              <Button
                variant="outlined"
                onClick={() => navigate(`/${'md'}+${data.claim_info?.username}`)}
              >
                View Profile
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate(`/claim/${p.npi}`)}
              >
                Claim this Profile
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ProviderPage;
